import { useState } from 'react';
import { osaseFileExtensions } from '@common/utils/fileExtensions';
import type { ICustomControlRequestAddEditProps } from '../../../interfaces/';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../../translate';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../../elements/common';
import { FormContextProvider } from '../../../../../elements/formContext';
import { TextAreaField } from '../../../../../fields/TextArea';
import { AssetDropzoneWithActions } from '../../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../../utils/v1/CareProviderSelector';
import { NewOrEditRequestProcessContainer } from '../../../utils/v1/NewOrEditRequestProcessContainer';

export function OsaseV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                    hideCorrectGpWarning={true}
                />

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'procedure',
                        item: props.request.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <TextAreaField
                        fieldKey="anamnese"
                        label={translateKey('carePlan.anamnese')}
                        placeholder="Vul hier de anamnese in..."
                    />

                    <TextAreaField
                        fieldKey="onderzoek"
                        label={translateKey('procedure.procedure')}
                        placeholder="Vul hier alle onderzoeksresultaten in..."
                    />

                    <TextAreaField
                        fieldKey="diagnose"
                        label={translateKey('diagnosis.diagnosis')}
                        placeholder="Vul hier je werkdiagnose en mogelijke differentiaaldiagnoses in..."
                    />

                    <TextAreaField
                        error={props.errors.get('customControlData.beleid')}
                        fieldKey="beleid"
                        label={translateKey('diagnosticReport.policy')}
                        placeholder="Vul hier het beleid in..."
                    />

                    <AssetDropzoneWithActions
                        acceptedFiles={osaseFileExtensions}
                        assets={props.request.assets}
                        assetsBaseUrl={FilesUrlBase.requests}
                        onChange={async (assets) => await props.handleChange('assets', assets)}
                        onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                        processActivityId={props.request.id}
                        service={props.service}
                        showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                        updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                    />
                </FormContextProvider>

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }
        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await props.onSave(requestId);
    }
}

export function osaseV1RequestInitializeCustomControlData() {
    return {
        anamnese: null,
        onderzoek: null,
        diagnose: null,
        beleid: null,
    };
}
